import { useState, useEffect } from 'react';
import { 
  getAuth, 
  onAuthStateChanged, 
  signOut, 
  GoogleAuthProvider, 
  signInWithPopup,
  signInWithCredential
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { 
  doc, 
  setDoc, 
  getDoc, 
  onSnapshot, 
  collection 
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, auth } from '../firebase';

// Helper to check if profile photo exists in Storage
const getProfilePhotoFromStorage = async (uid) => {
  const storage = getStorage();
  const imageRef = ref(storage, `profiles/${uid}.webp`);
  try {
    return await getDownloadURL(imageRef);
  } catch (err) {
    return null;
  }
};

// Helper to download Google photo and save to Storage as WebP
const saveGooglePhotoAsWebp = async (uid, googlePhotoURL) => {
  const storage = getStorage();
  const response = await fetch(googlePhotoURL);
  const blob = await response.blob();
  const imageRef = ref(storage, `profiles/${uid}.webp`);
  await uploadBytes(imageRef, blob, {
    contentType: "image/webp",
  });
  return await getDownloadURL(imageRef);
};

// Helper to create or update user documents in Firestore
const createOrUpdateUserDocument = async (user) => {
  if (!user) return;

  const userDocRef = doc(db, "users", user.uid);
  const publicDocRef = doc(db, "users_public", user.uid);
  const now = new Date();

  try {
    const userDoc = await getDoc(userDocRef);
    const publicDoc = await getDoc(publicDocRef);

    let finalPhotoURL = null;
    let hasPhotoInStorage = false;

    const userData = userDoc.exists() ? userDoc.data() : null;
    const existingPhotoPath = userData?.photoPath;

    if (existingPhotoPath) {
      finalPhotoURL = userData.photoURL;
      hasPhotoInStorage = true;
    } else {
      finalPhotoURL = await getProfilePhotoFromStorage(user.uid);
      hasPhotoInStorage = !!finalPhotoURL;

      if (!finalPhotoURL && user.photoURL) {
        try {
          finalPhotoURL = await saveGooglePhotoAsWebp(user.uid, user.photoURL);
          hasPhotoInStorage = true;
        } catch (err) {
          console.error("❌ Erro ao copiar foto do Google:", err);
          finalPhotoURL = user.photoURL;
          hasPhotoInStorage = false;
        }
      }
    }

    const photoPath = hasPhotoInStorage ? `profiles/${user.uid}.webp` : null;

    // Auto-heal de allowedFeatures para contas institucionais/Unifesp/Carboplant
    const shouldEnableProFeatures =
      user.uid === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
      user.email === "lab.carboplant@gmail.com" ||
      userData?.role === "pro_universidade";

    const featuresPatch = shouldEnableProFeatures
      ? { allowedFeatures: { confidentialFields: true, teamManagement: true } }
      : {};

    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        displayName: user.displayName || "No name",
        email: user.email,
        photoURL: finalPhotoURL || null,
        photoPath: photoPath,
        lastLogin: now,
        acceptUserTerms: false,
        isAdmin: false,
        ...featuresPatch,
      });
    } else {
      await setDoc(
        userDocRef,
        {
          lastLogin: now,
          photoURL: finalPhotoURL || null,
          photoPath: photoPath,
          ...featuresPatch,
        },
        { merge: true }
      );
    }

    if (!publicDoc.exists()) {
      await setDoc(publicDocRef, {
        displayName: user.displayName || "No name",
        photoURL: finalPhotoURL || null,
        photoPath: photoPath,
        lastLogin: now,
        date: now,
        ...featuresPatch,
      });
    } else {
      await setDoc(
        publicDocRef,
        {
          lastLogin: now,
          photoURL: finalPhotoURL || null,
          photoPath: photoPath,
          ...featuresPatch,
        },
        { merge: true }
      );
    }
  } catch (error) {
    console.error("❌ Erro no createOrUpdateUserDocument:", error);
  }
};

export function useAppAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [acceptUserTerms, setAcceptUserTerms] = useState(false);
  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState("");
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [photoURL, setPhotoUrl] = useState('');
  const [displayFinal, setDisplayFinal] = useState('');
  const [isUserDataLoading, setIsUserDataLoading] = useState(true);

  // Sign In with Google
  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle();

        if (result.credential?.idToken) {
          const credential = GoogleAuthProvider.credential(result.credential.idToken);
          await signInWithCredential(auth, credential);
        }
      } else {
        const provider = new GoogleAuthProvider();
        provider.addScope('profile');
        provider.addScope('email');
        const result = await signInWithPopup(auth, provider);
      }
    } catch (error) {
      console.error('Erro no login:', error);
      const errorCode = error?.code || error?.message;
      if (errorCode === 'auth/popup-closed-by-user' || errorCode === 'auth/cancelled' || error === 'user_cancelled') {
      } else {
        alert('Erro no login: ' + (error.message || error));
      }
    } finally {
      setIsLoading(false);
    }
  };


  // Sign Out
  const handleSignOut = () => {
    const authInstance = getAuth();
    const currentUid = authInstance.currentUser?.uid;
    if (currentUid) {
      localStorage.removeItem(`user_cache_${currentUid}`);
    }
    signOut(authInstance)
      .then(() => {
        alert("Logout realizado com sucesso!");
        setUser(null);
        setIsAdmin(false);
        setAcceptUserTerms(false);
        window.location.href = "/welcome";
      })
      .catch((error) => console.error("Sign Out Error", error));
  };

  useEffect(() => {
    const authInstance = getAuth();
    let unsubscribeFirestore = null;

    const unsubscribeAuth = onAuthStateChanged(authInstance, async (authUser) => {
      if (authUser) {
        setUser(authUser);

        // 1) Carregar dados em cache local imediatamente para login offline instantâneo
        const cacheKey = `user_cache_${authUser.uid}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            setAcceptUserTerms(parsed.acceptUserTerms || false);
            setAcceptedTermsVersion(parsed.acceptedTermsVersion || "");
            setDisplayFinal(parsed.displayFinal || "");
            setPhotoUrl(parsed.photoURL || "");
            setIsAdmin(parsed.isAdmin || false);
          } catch (e) {
            console.warn("Erro ao ler cache local de autenticação:", e);
          }
        }

        try {
          if (typeof navigator !== 'undefined' && navigator.onLine) {
            createOrUpdateUserDocument(authUser).catch(err => console.error(err));
          }

          // 2) Verificar claims PRO/Admin de forma assíncrona (não bloqueante para o snapshot)
          authUser.getIdTokenResult()
            .then((tokenResult) => {
              const role = tokenResult.claims?.role ?? "user";
              const isAdminRole = Boolean(
                role === "admin" ||
                tokenResult.claims?.isAdmin === true ||
                tokenResult.claims?.admin === true
              );
              if (isAdminRole) {
                setIsAdmin(true);
              }

              // Atualiza o campo isAdmin no cache local
              const currentCache = localStorage.getItem(cacheKey);
              if (currentCache) {
                try {
                  const parsed = JSON.parse(currentCache);
                  parsed.isAdmin = parsed.isAdmin || isAdminRole;
                  localStorage.setItem(cacheKey, JSON.stringify(parsed));
                } catch (e) {}
              }
            })
            .catch((err) => {
              console.warn("Não foi possível verificar custom claims (offline):", err);
            });

          const userDocRef = doc(db, "users", authUser.uid);
          setIsUserDataLoading(true);

          if (unsubscribeFirestore) unsubscribeFirestore();

          unsubscribeFirestore = onSnapshot(
            userDocRef,
            async (docSnapshot) => {
              if (docSnapshot.exists()) {
                const userData = docSnapshot.data();
                const termsAccepted = userData.acceptUserTerms || false;
                const termsVersion = userData.acceptedTermsVersion || "";
                const finalName =
                  userData.customDisplayName?.trim() ||
                  userData.displayName?.trim() ||
                  authUser.displayName ||
                  "";
                const isAdminDoc = Boolean(
                  userData.isAdmin === true ||
                  userData.role === "admin" ||
                  userData.user_type === "admin"
                );

                setAcceptUserTerms(termsAccepted);
                setAcceptedTermsVersion(termsVersion);
                setDisplayFinal(finalName);
                setIsAdmin((prev) => Boolean(prev || isAdminDoc));

                let finalPhoto = userData.photoURL || null;
                if (typeof navigator !== 'undefined' && navigator.onLine) {
                  try {
                    const storagePhotoURL = await getProfilePhotoFromStorage(authUser.uid);
                    finalPhoto = storagePhotoURL || userData.photoURL || null;
                  } catch (err) {
                    console.error("❌ Erro ao buscar foto no storage:", err);
                  }
                }
                setPhotoUrl(finalPhoto);

                // 3) Gravar/atualizar dados no cache do localStorage
                const cacheData = {
                  acceptUserTerms: termsAccepted,
                  acceptedTermsVersion: termsVersion,
                  displayFinal: finalName,
                  photoURL: finalPhoto,
                  isAdmin: isAdminDoc,
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
              }
              setIsUserDataLoading(false);
              setIsLoading(false);
            },
            (error) => {
              console.error("❌ Erro no listener do Firestore userDoc:", error);
              setIsUserDataLoading(false);
              setIsLoading(false);
            }
          );
        } catch (err) {
          console.error("❌ Erro ao processar dados do usuário pós-login:", err);
          setIsUserDataLoading(false);
          setIsLoading(false);
        }
      } else {
        if (unsubscribeFirestore) {
          unsubscribeFirestore();
          unsubscribeFirestore = null;
        }
        setUser(null);
        setIsAdmin(false);
        setAcceptUserTerms(false);
        setAcceptedTermsVersion("");
        setNotificationsCount(0);
        setIsLoading(false);
        setIsUserDataLoading(false);
      }
    });

    return () => {
      if (unsubscribeFirestore) unsubscribeFirestore();
      unsubscribeAuth();
    };
  }, []);


  // Notifications listener
  useEffect(() => {
    if (!user) {
      setNotificationsCount(0);
      return;
    }

    const notiRef = collection(db, "users", user.uid, "notifications");
    const unsubscribe = onSnapshot(notiRef, (snapshot) => {
      let count = 0;
      snapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        if (!("read" in data) || data.read === false) {
          count++;
        }
      });
      setNotificationsCount(count);
    });

    return () => unsubscribe();
  }, [user]);

  return {
    user,
    isLoading,
    isAdmin,
    acceptUserTerms,
    acceptedTermsVersion,
    notificationsCount,
    photoURL,
    displayFinal,
    isUserDataLoading,
    signInWithGoogle,
    handleSignOut
  };
}
